import { cardPaymentRequestSchema, customerCheckoutViewSchema, fakeGatewayCheckoutResultSchema, PAYMENT_ROUTE_PATHS, paymentCancelRefusalEnvelopeSchema, paymentCheckoutErrorSchema, paymentCheckoutRefusalEnvelopeSchema, } from "@lafayette/schemas";
import { createJsonTransport } from "./json-transport";
import { createMemoryTransport, MEMORY_ERRORS, MemoryApiError, MemoryApiResponseError, } from "./memory-transport";
/** A checkout or cancel refusal. `payment` is `null` when the body had no valid `payment` key (an older server, a proxy page). */
export class PaymentApiError extends MemoryApiError {
    payment;
    constructor(base, payment) {
        super(base.status, base.code, base.message, base.requestId, {
            conflict: base.conflict,
            invalidField: base.invalidField,
            tripRefusal: base.tripRefusal,
            requestConflict: base.requestConflict,
            itineraryRefusal: base.itineraryRefusal,
            retryAfter: base.retryAfter,
        });
        this.name = "PaymentApiError";
        this.payment = payment;
    }
    get conflictCode() {
        return this.payment?.code ?? super.conflictCode;
    }
    get detail() {
        return { ...super.detail, payment: this.payment };
    }
}
const checkoutPendingSchema = {
    safeParse: (input) => {
        const view = customerCheckoutViewSchema.safeParse(input);
        if (view.success)
            return { success: true, data: { kind: "ready", view: view.data } };
        const pending = paymentCheckoutErrorSchema.safeParse(input);
        if (pending.success && pending.data.code === "payment.order_pending") {
            return { success: true, data: { kind: "pending", error: pending.data } };
        }
        return { success: false };
    },
};
function refusalPayment(json) {
    const checkout = paymentCheckoutRefusalEnvelopeSchema.safeParse(json);
    if (checkout.success)
        return checkout.data;
    const cancel = paymentCancelRefusalEnvelopeSchema.safeParse(json);
    return cancel.success ? cancel.data : null;
}
/** The raw `data` of a 2xx answer. The caller parses it, so a body it does not know is a `MemoryApiResponseError` there. */
const anyData = { safeParse: (data) => ({ success: true, data }) };
/** A refusal of a checkout or cancel is a `PaymentApiError`: `payment` is `null` when the body had no valid `payment` key. */
const PAYMENT_ERRORS = {
    ...MEMORY_ERRORS,
    refusedMessage: "the payment command was refused",
    http: (input) => {
        const refusal = refusalPayment(input.body);
        const base = MEMORY_ERRORS.http({
            ...input,
            message: "the payment command was refused",
            ...(refusal === null
                ? {}
                : { code: refusal.error.code, requestId: refusal.requestId }),
        });
        return new PaymentApiError(base, refusal?.payment ?? null);
    },
};
export function createCustomerPaymentsApi(options) {
    const send = createMemoryTransport(options);
    // A command that can be refused with a `payment` key has its own transport: the family reads that key. It makes no network retry: no
    // answer is NOT a refusal. The caller keeps its key and taps again, and the server gives the same answer. The body is always the one
    // the caller names here, and it never holds an amount.
    const sendPayment = createJsonTransport(options, PAYMENT_ERRORS);
    const post = (path, body, commandOptions) => sendPayment({
        method: "POST",
        path,
        body,
        schema: anyData,
        idempotencyKey: commandOptions?.idempotencyKey,
        signal: commandOptions?.signal,
        maxNetworkRetries: 0,
    });
    const paymentPath = (template, paymentRequestId) => template.replace(":paymentRequestId", encodeURIComponent(paymentRequestId));
    return {
        getPayment: (paymentRequestId, callOptions) => send({
            method: "GET",
            path: `/customer/payment-requests/${encodeURIComponent(paymentRequestId)}`,
            schema: cardPaymentRequestSchema,
            signal: callOptions?.signal,
        }),
        async createPayment(input, commandOptions) {
            // The body names the item and the links only. There is no field for an amount, a currency or a status.
            const body = { catalogItemId: input.catalogItemId };
            if (input.conciergeRequestId)
                body.conciergeRequestId = input.conciergeRequestId;
            if (input.retryOfPaymentRequestId)
                body.retryOfPaymentRequestId = input.retryOfPaymentRequestId;
            const data = await post("/customer/payment-requests", body, commandOptions);
            const parsed = cardPaymentRequestSchema.safeParse(data);
            if (!parsed.success)
                throw new MemoryApiResponseError();
            return parsed.data;
        },
        async startCheckout(paymentRequestId, commandOptions) {
            // The body is always empty: the amount, the currency and the order come from the server.
            const data = await post(paymentPath(PAYMENT_ROUTE_PATHS.customerCheckout, paymentRequestId), {}, commandOptions);
            const parsed = checkoutPendingSchema.safeParse(data);
            if (!parsed.success)
                throw new MemoryApiResponseError();
            return parsed.data.kind === "ready"
                ? { kind: "ready", checkout: parsed.data.view }
                : { kind: "pending", currentStatus: parsed.data.error.currentStatus };
        },
        async cancelPayment(paymentRequestId, commandOptions) {
            const data = await post(paymentPath(PAYMENT_ROUTE_PATHS.customerCancel, paymentRequestId), {}, commandOptions);
            const parsed = cardPaymentRequestSchema.safeParse(data);
            if (!parsed.success)
                throw new MemoryApiResponseError();
            return parsed.data;
        },
        fakeCheckout: (paymentRequestId, action, commandOptions) => send({
            method: "POST",
            path: PAYMENT_ROUTE_PATHS.fakeCheckout,
            body: { paymentRequestId, action },
            schema: fakeGatewayCheckoutResultSchema,
            idempotencyKey: commandOptions?.idempotencyKey,
            signal: commandOptions?.signal,
        }),
    };
}
